import React, { useState } from 'react'

interface LedgerTableHeaderProps {
  clientName: string
  fromDate: string
  toDate: string
  openingPoints?: string | number
  openingMoney?: string | number
  closingPoints?: string | number
  closingMoney?: string | number
  profile_image_url?: string
  theme?: string
}

const LedgerTableHeader: React.FC<LedgerTableHeaderProps> = ({
  clientName,
  fromDate,
  toDate,
  openingPoints,
  openingMoney,
  closingPoints,
  closingMoney,
  profile_image_url,
  theme = 'primary',
}) => {
  const [imageError, setImageError] = useState(false)
  return (
    <div
      className='card-header py-5 flex-wrap flex-md-nowrap w-100 shadow-sm'
      style={{
        position: 'relative',
        paddingLeft: '20px',
        background: 'linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%)',
      }}
    >
      {/* Left vertical accent bar */}
      <span
        className={`position-absolute border-${theme}`}
        style={{
          top: '10%',
          left: '0',
          height: '80%',
          width: '5px',
          backgroundColor: `var(--bs-${theme})`,
          borderRadius: '0 4px 4px 0',
        }}
      ></span>

      <div className='d-flex flex-column gap-4 w-100'>
        {/* Client Name & Date Range Row */}
        <div className='d-flex flex-wrap justify-content-between align-items-center gap-3'>
          {/* Client Name */}
          <div className='d-flex align-items-center gap-3'>
            {profile_image_url && !imageError ? (
              <div
                className='d-flex align-items-center justify-content-center'
                style={{
                  width: '50px',
                  height: '50px',
                  borderRadius: '50%',
                  overflow: 'hidden',
                  border: `1px solid var(--bs-${theme})`,
                  flexShrink: 0,
                }}
              >
                <img
                  src={profile_image_url}
                  alt={clientName}
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                  }}
                  onError={() => {
                    // Fallback to icon if image fails to load
                    setImageError(true)
                  }}
                />
              </div>
            ) : (
              <div
                className='d-flex align-items-center justify-content-center'
                style={{
                  width: '50px',
                  height: '50px',
                  borderRadius: '50%',
                  backgroundColor: `var(--bs-${theme})`,
                  opacity: 0.1,
                }}
              >
                <i className={`bi bi-person-circle fs-2 text-${theme}`}></i>
              </div>
            )}
            <div>
              <div className='text-muted fw-semibold fs-7 mb-1'>Client Ledger</div>
              <div className='fs-3 fw-bold text-gray-800 text-capitalize'>{clientName}</div>
            </div>
          </div>

          {/* Date Range */}
          <div className='d-flex align-items-center gap-3 bg-white px-4 py-3 rounded shadow-sm'>
            <div className='d-flex align-items-center gap-2'>
              <i className='bi bi-calendar-range-fill text-primary fs-5'></i>
              <div className='d-flex flex-column'>
                <span className='text-muted fw-semibold' style={{fontSize: '0.7rem'}}>
                  From
                </span>
                <span className='fw-bold text-dark fs-7'>{fromDate}</span>
              </div>
            </div>
            <div
              className='border-end'
              style={{height: '35px', borderColor: '#e4e6ef !important'}}
            ></div>
            <div className='d-flex flex-column'>
              <span className='text-muted fw-semibold' style={{fontSize: '0.7rem'}}>
                To
              </span>
              <span className='fw-bold text-dark fs-7'>{toDate}</span>
            </div>
          </div>
        </div>

        {/* Balance Cards Row */}
        <div className='d-flex flex-wrap gap-3'>
          {/* Opening Balance Card */}
          <div
            className='flex-fill bg-white rounded p-3 shadow-sm'
            style={{minWidth: '280px', border: '1px solid #e4e6ef'}}
          >
            <div className='d-flex align-items-center gap-2 mb-2'>
              <i className='bi bi-arrow-bar-right text-success fs-4'></i>
              <span className='text-muted fw-semibold fs-6'>Opening Balance</span>
            </div>
            <div className='d-flex justify-content-between align-items-center'>
              <div className='d-flex flex-column'>
                <span className='text-muted fw-semibold' style={{fontSize: '0.7rem'}}>
                  Points
                </span>
                <span className='text-success fw-bold fs-4'>
                  <i className='bi bi-wallet2 me-1'></i>
                  {openingPoints ?? 0}
                </span>
              </div>
              <div
                className='border-end'
                style={{height: '40px', borderColor: '#e4e6ef !important'}}
              ></div>
              <div className='d-flex flex-column'>
                <span className='text-muted fw-semibold' style={{fontSize: '0.7rem'}}>
                  Money
                </span>
                <span className='text-success fw-bold fs-4'>
                  <i className='bi bi-currency-rupee me-1'></i>
                  {openingMoney
                    ? Number(openingMoney).toLocaleString('en-IN', {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })
                    : '0.00'}
                </span>
              </div>
            </div>
          </div>

          {/* Closing Balance Card */}
          <div
            className='flex-fill bg-white rounded p-3 shadow-sm'
            style={{minWidth: '280px', border: '1px solid #e4e6ef'}}
          >
            <div className='d-flex align-items-center gap-2 mb-2'>
              <i className='bi bi-arrow-bar-left text-primary fs-4'></i>
              <span className='text-muted fw-semibold fs-6'>Closing Balance</span>
            </div>
            <div className='d-flex justify-content-between align-items-center'>
              <div className='d-flex flex-column'>
                <span className='text-muted fw-semibold' style={{fontSize: '0.7rem'}}>
                  Points
                </span>
                <span className={`text-${theme} fw-bold fs-4`}>
                  <i className='bi bi-wallet2 me-1'></i>
                  {closingPoints ?? 0}
                </span>
              </div>
              <div
                className='border-end'
                style={{height: '40px', borderColor: '#e4e6ef !important'}}
              ></div>
              <div className='d-flex flex-column'>
                <span className='text-muted fw-semibold' style={{fontSize: '0.7rem'}}>
                  Money
                </span>
                <span className={`text-${theme} fw-bold fs-4`}>
                  <i className='bi bi-currency-rupee me-1'></i>
                  {closingMoney
                    ? Number(closingMoney).toLocaleString('en-IN', {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })
                    : '0.00'}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default LedgerTableHeader
